import type { TemplateContext } from '../types/index.js';

/**
 * Roots a `{{root.path}}` placeholder may begin with. Any other root resolves
 * to `undefined` (renders as `''`), which is the silent-failure mode the
 * strict template validator catches via {@link findInvalidRoots}.
 */
export const VALID_TEMPLATE_ROOTS = [
  'input',
  'trigger',
  'state',
  'nodes',
  'iteration',
  'memory',
  'run',
] as const;

export type TemplateRoot = (typeof VALID_TEMPLATE_ROOTS)[number];

const VALID_TEMPLATE_ROOT_SET: ReadonlySet<string> = new Set(VALID_TEMPLATE_ROOTS);

/**
 * Resolve a template string by replacing {{path.to.value}} placeholders
 * with values from the provided context.
 *
 * Supported paths:
 *   {{input.fieldName}}              — upstream node output (or trigger data for entry nodes)
 *   {{trigger.issueTitle}}           — original trigger data (immutable)
 *   {{state.plan}}                   — mutable run-level state
 *   {{nodes.nodeId.output.summary}}  — explicit node output by ID
 *   {{nodes.nodeId.approvalResponse.feedback}}
 *   {{iteration}}
 *   {{memory.relevant}}
 *   {{run.id}}                       — id of the current run
 *   {{run.url}}                      — link to the current run's page ('' when unresolvable)
 *
 * Conditional blocks are also supported:
 *   {{#if trigger.flag}} … {{/if}}
 *   {{#if nodes.x.output.y}} … {{else}} … {{/if}}
 * The condition is a path resolved against the same context and tested for
 * truthiness (undefined/null/false/''/0/empty-array are falsy). Blocks may
 * nest. Only the selected branch is kept; its inner {{path}} placeholders are
 * then substituted as usual. The conditional pass runs before substitution and
 * substituted values are never re-scanned, so template syntax inside resolved
 * values (node outputs, state) is inert text — never control flow.
 */
export function resolveTemplate(template: string, context: TemplateContext): string {
  // The `#if` substring gate is a superset of the tokenizer's trimmed-body
  // recognition (so spaced `{{ #if … }}` still gates). Non-conditional
  // templates skip the pass entirely; prose mentioning `#if` runs the linear
  // pass as a no-op.
  const rendered = template.includes('#if') ? renderConditionals(template, context) : template;

  // Disallow `{` inside the placeholder body so the regex is strictly linear
  // (avoids polynomial ReDoS backtracking on inputs like `{{{{{{...`).
  return rendered.replace(/\{\{([^{}]+)\}\}/g, (_match, path: string) => {
    const trimmed = path.trim();
    const value = resolvePath(trimmed, context);
    if (value === undefined || value === null) return '';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  });
}

type BodyKind =
  | { kind: 'if'; cond: string }
  | { kind: 'else' }
  | { kind: 'endif' }
  | { kind: 'path'; path: string };

// `#if` opens a conditional only when followed by whitespace and a condition.
// `\s`/`\S` are disjoint classes, so the match is strictly linear.
const IF_PREFIX = /^#if\s+\S/;

/**
 * Classify a trimmed placeholder body. Single classification shared by the
 * tokenizer and `extractTemplatePaths` so the render grammar and the
 * validation grammar cannot drift. Exactly three control forms exist:
 * `#if <cond>`, `else`, `/if`. Anything else — bare `#if`, unknown
 * `#helper`s, `/helper` closers — is a plain path, which the strict validator
 * flags via {@link findInvalidRoots} and render substitutes to `''`.
 */
function classifyBody(body: string): BodyKind {
  if (body === 'else') return { kind: 'else' };
  if (body === '/if') return { kind: 'endif' };
  if (IF_PREFIX.test(body)) return { kind: 'if', cond: body.slice(3).trim() };
  return { kind: 'path', path: body };
}

type ControlToken =
  | { type: 'text'; value: string }
  | { type: 'if'; cond: string }
  | { type: 'else' }
  | { type: 'endif' };

/**
 * Split a template into literal text and `{{#if …}}` / `{{else}}` / `{{/if}}`
 * control tokens. Uses the same single linear scanner as `resolveTemplate` and
 * `extractTemplatePaths` — the `[^{}]` body constraint forbids backtracking, so
 * there is no polynomial blow-up on malformed input. Non-control `{{path}}`
 * placeholders are left inside the surrounding text run for the later
 * substitution pass.
 */
function tokenizeControl(template: string): ControlToken[] {
  const tokens: ControlToken[] = [];
  const re = /\{\{([^{}]+)\}\}/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(template)) !== null) {
    const c = classifyBody(m[1].trim());
    // Plain placeholders stay in the text run (don't advance `last`).
    if (c.kind === 'path') continue;
    if (m.index > last) tokens.push({ type: 'text', value: template.slice(last, m.index) });
    if (c.kind === 'if') tokens.push({ type: 'if', cond: c.cond });
    else tokens.push({ type: c.kind });
    last = re.lastIndex;
  }
  if (last < template.length) tokens.push({ type: 'text', value: template.slice(last) });
  return tokens;
}

/**
 * Resolve `{{#if}}` blocks to their selected branch, leaving `{{path}}`
 * placeholders intact. Single pass with an explicit block stack — no
 * recursion, so nesting depth is bounded only by template length. Malformed
 * input renders predictably: a stray `{{/if}}` or `{{else}}` with no open
 * block is dropped; an unclosed `{{#if}}` renders its selected branch to the
 * end of the template.
 */
function renderConditionals(template: string, context: TemplateContext): string {
  const frames: Array<{ condTruthy: boolean; parentEmitting: boolean; inElse: boolean }> = [];
  let emitting = true;
  let out = '';
  for (const token of tokenizeControl(template)) {
    if (token.type === 'text') {
      if (emitting) out += token.value;
    } else if (token.type === 'if') {
      // Short-circuit: conditions inside a dead branch are never resolved.
      const condTruthy: boolean = emitting && isTruthyValue(resolvePath(token.cond, context));
      frames.push({ condTruthy, parentEmitting: emitting, inElse: false });
      emitting = condTruthy;
    } else if (token.type === 'else') {
      const top = frames[frames.length - 1];
      // A second `{{else}}` in the same block keeps the else branch.
      if (top && !top.inElse) {
        top.inElse = true;
        emitting = top.parentEmitting && !top.condTruthy;
      }
    } else {
      const top = frames.pop();
      if (top) emitting = top.parentEmitting;
    }
  }
  return out;
}

/**
 * Truthiness of a raw resolved value. Shared by `{{#if}}` gating here and the
 * filter evaluator's `truthy` op so both treat empty string, `0`, `false`,
 * `null`, and empty arrays as falsy. Strings defer to
 * {@link isTruthyResolvedString}.
 */
export function isTruthyValue(value: unknown): boolean {
  if (value === undefined || value === null || value === false) return false;
  if (typeof value === 'string') return isTruthyResolvedString(value);
  if (typeof value === 'number') return value !== 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

/**
 * Resolve a dotted context path to its RAW value (no stringification).
 * Used by `resolveTemplate` for placeholder substitution and by the filter
 * evaluator, which needs unmodified arrays/objects for ops like `intersects`.
 */
export function resolvePath(path: string, context: TemplateContext): unknown {
  const parts = path.split('.');
  const root = parts[0];

  if (!VALID_TEMPLATE_ROOT_SET.has(root)) return undefined;

  switch (root) {
    case 'input':
      return getNestedValue(context.input, parts.slice(1));
    case 'trigger':
      return getNestedValue(context.trigger, parts.slice(1));
    case 'state':
      return getNestedValue(context.state, parts.slice(1));
    case 'nodes':
      return resolveNodePath(parts.slice(1), context);
    case 'iteration':
      return context.iteration;
    case 'memory':
      return getNestedValue(context.memory, parts.slice(1));
    case 'run':
      return getNestedValue(context.run, parts.slice(1));
    default:
      return undefined;
  }
}

function resolveNodePath(parts: string[], context: TemplateContext): unknown {
  if (parts.length < 2 || !context.nodes) return undefined;
  const [nodeId, ...rest] = parts;
  const nodeData = context.nodes[nodeId];
  if (!nodeData) return undefined;
  return getNestedValue(nodeData, rest);
}

function getNestedValue(obj: unknown, parts: string[]): unknown {
  if (obj === undefined || obj === null || parts.length === 0) return obj;

  const [head, ...rest] = parts;
  if (typeof obj !== 'object') return undefined;

  const record = obj as Record<string, unknown>;
  return getNestedValue(record[head], rest);
}

/**
 * Truthiness of an already-resolved template string. Shared by the router
 * expression evaluator and filter nodes so "{{trigger.flag}}" gates behave
 * identically in both.
 */
export function isTruthyResolvedString(value: string): boolean {
  const trimmed = value.trim();
  return (
    trimmed !== '' &&
    trimmed !== 'false' &&
    trimmed !== '0' &&
    trimmed !== 'null' &&
    trimmed !== 'undefined'
  );
}

/**
 * Extract all template variable paths from a template string. Useful for
 * validation and dependency analysis. `{{#if <path>}}` contributes its
 * condition path (so its root and node refs are validated); `{{else}}` /
 * `{{/if}}` contribute nothing. Unsupported helpers (`{{#each …}}`,
 * `{{/each}}`, bare `{{#if}}`) come through as plain paths so
 * {@link findInvalidRoots} rejects them at save time instead of letting them
 * silently render as empty markers.
 */
export function extractTemplatePaths(template: string): string[] {
  const paths: string[] = [];
  // Disallow `{` inside the placeholder body so the regex stays strictly
  // linear, matching `resolveTemplate` above (avoids polynomial ReDoS
  // backtracking on inputs like `{{{{{{...`).
  const regex = /\{\{([^{}]+)\}\}/g;
  let match;
  while ((match = regex.exec(template)) !== null) {
    const c = classifyBody(match[1].trim());
    if (c.kind === 'if') paths.push(c.cond);
    else if (c.kind === 'path') paths.push(c.path);
  }
  return paths;
}

/**
 * Return the distinct invalid roots referenced by a template string — the
 * first segment of any `{{root.path}}` placeholder whose root is not in
 * {@link VALID_TEMPLATE_ROOTS}. An empty array means every placeholder uses a
 * recognized root. `{{iteration}}` (no dot) is valid and excluded.
 */
export function findInvalidRoots(template: string): string[] {
  const invalid = new Set<string>();
  for (const path of extractTemplatePaths(template)) {
    const root = path.split('.')[0];
    if (root && !VALID_TEMPLATE_ROOT_SET.has(root)) invalid.add(root);
  }
  return [...invalid];
}
